/**
 * Student join: class code -> pick your name -> 4-digit PIN. The PIN pad has big keys (iPad), and a real
 * input underneath so a keyboard also works. After 5 wrong PINs the server locks that student for 5 minutes.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { CLASS_CODE_LENGTH, PIN_LENGTH, type JoinLookupResponse } from '@shared/protocol.ts';
import { cleanCode } from '../../lib/text';
import { api, ApiRequestError, errorText } from '../../net/api';
import { sessions } from '../../net/session';
import { href, navigate } from '../../router';
import { Avatar } from '../../ui/Avatar';
import { Button, IconButton } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { Callout, CardButton } from '../../ui/primitives';
import { Screen, Stack } from '../../ui/layout';
import { useReducedMotion } from '../../ui/hooks';

type Step = 'code' | 'name' | 'pin';

export function JoinScreen({ initialCode }: { initialCode?: string }) {
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState(cleanCode(initialCode ?? ''));
  const [world, setWorld] = useState<JoinLookupResponse | null>(null);
  const [student, setStudent] = useState<{ id: string; name: string } | null>(null);
  const reduce = useReducedMotion();

  const anim = reduce
    ? {}
    : { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.22 } };

  return (
    <Screen max={640} label="Join your class">
      <div className="join">
        <header className="join__header">
          <a className="btn btn--ghost" href={href()} aria-label="Back to the start page">
            <Icon name="arrow_left" size={20} />
            <span className="btn__label">Sovereign</span>
          </a>
          <ol className="join__steps" aria-label="Steps">
            {(['code', 'name', 'pin'] as const).map((s, i) => (
              <li key={s} className={step === s ? 'is-current' : (['code', 'name', 'pin'].indexOf(step) > i ? 'is-done' : '')} aria-current={step === s ? 'step' : undefined}>
                <span className="visually-hidden">Step {i + 1}: </span>
                {s === 'code' ? 'Class code' : s === 'name' ? 'Your name' : 'PIN'}
              </li>
            ))}
          </ol>
        </header>
        <AnimatePresence mode="wait" initial={false}>
          {step === 'code' && (
            <motion.div key="code" {...anim}>
              <CodeStep
                code={code}
                setCode={setCode}
                onFound={(w) => {
                  setWorld(w);
                  setStep('name');
                }}
              />
            </motion.div>
          )}
          {step === 'name' && world && (
            <motion.div key="name" {...anim}>
              <NameStep
                world={world}
                onBack={() => setStep('code')}
                onPick={(s) => {
                  setStudent(s);
                  setStep('pin');
                }}
              />
            </motion.div>
          )}
          {step === 'pin' && world && student && (
            <motion.div key="pin" {...anim}>
              <PinStep world={world} student={student} classCode={code} onBack={() => setStep('name')} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Screen>
  );
}

function CodeStep({ code, setCode, onFound }: { code: string; setCode: (c: string) => void; onFound: (w: JoinLookupResponse) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (code.length !== CLASS_CODE_LENGTH || busy) return;
    setBusy(true);
    setError(null);
    try {
      const w = await api.joinLookup(code);
      onFound(w);
    } catch (err) {
      setError(err instanceof ApiRequestError && err.code === 'not_found' ? 'We could not find a class with that code. Check it and try again.' : errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="join__card card card--pad card--raised">
      <Stack gap={4}>
        <div>
          <h1 className="join__title">Join your class</h1>
          <p className="muted">Type the class code your teacher gave you.</p>
        </div>
        <label className="visually-hidden" htmlFor="class-code">
          Class code, {CLASS_CODE_LENGTH} characters
        </label>
        <div className="code-boxes" onClick={() => inputRef.current?.focus()}>
          <input
            ref={inputRef}
            id="class-code"
            className="code-boxes__input"
            value={code}
            onChange={(e) => {
              setCode(cleanCode(e.target.value));
              setError(null);
            }}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={CLASS_CODE_LENGTH + 4}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'code-error' : undefined}
          />
          {Array.from({ length: CLASS_CODE_LENGTH }, (_, i) => (
            <span key={i} className={code.length === i ? 'code-box is-active' : 'code-box'} aria-hidden>
              {code[i] ?? ''}
            </span>
          ))}
        </div>
        {error && (
          <p id="code-error" className="field__error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={code.length !== CLASS_CODE_LENGTH} iconAfter="arrow_right">
          Find my class
        </Button>
      </Stack>
    </form>
  );
}

function NameStep({ world, onBack, onPick }: { world: JoinLookupResponse; onBack: () => void; onPick: (s: { id: string; name: string }) => void }) {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const sorted = world.students.slice().sort((a, b) => a.name.localeCompare(b.name));
    const needle = q.trim().toLowerCase();
    return needle ? sorted.filter((s) => s.name.toLowerCase().includes(needle)) : sorted;
  }, [world.students, q]);
  return (
    <Stack gap={4}>
      <div className="row" style={{ gap: 8 }}>
        <IconButton icon="arrow_left" label="Back to the class code" onClick={onBack} />
        <div>
          <p className="eyebrow">{world.className}</p>
          <h1 className="join__title">Tap your name</h1>
        </div>
      </div>
      {world.students.length > 12 && (
        <input className="input" type="search" placeholder="Find your name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find your name" />
      )}
      {world.students.length === 0 ? (
        <Callout tone="warn" title="No names yet">
          Your teacher has not added the class list yet. Wait a moment, then go back and try again.
        </Callout>
      ) : (
        <ul className="name-grid" aria-label="Students in this class">
          {list.map((s) => (
            <li key={s.id}>
              <CardButton className="name-card" onClick={() => onPick(s)}>
                <Avatar name={s.name} size={40} />
                <span className="name-card__name">{s.name}</span>
              </CardButton>
            </li>
          ))}
        </ul>
      )}
      {list.length === 0 && world.students.length > 0 && <p className="muted">No name matches “{q}”.</p>}
      <p className="faint">Don’t see your name? Ask your teacher to add you.</p>
    </Stack>
  );
}

function PinStep({ world, student, classCode, onBack }: { world: JoinLookupResponse; student: { id: string; name: string }; classCode: string; onBack: () => void }) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const typedRef = useRef(false);

  const submit = async (value: string) => {
    if (value.length !== PIN_LENGTH || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.pinLogin(world.worldId, student.id, value);
      sessions.setStudent({ kind: 'student', worldId: r.worldId, token: r.token, studentId: r.studentId, name: r.name, classCode, className: world.className });
      navigate('/play', { replace: true });
    } catch (err) {
      setPin('');
      setShake((n) => n + 1);
      // The server's words say how many tries are left before the 5-minute lock.
      if (err instanceof ApiRequestError && err.code === 'wrong_pin') setError(err.message || 'That PIN is not right. Try again.');
      else setError(errorText(err));
      // Keyboard users go straight back to typing. Pad users keep the pad: focusing the hidden input would
      // open the on-screen keyboard over it on an iPad.
      if (typedRef.current) inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const press = (d: string) => {
    if (busy) return;
    typedRef.current = false;
    setError(null);
    const next = (pin + d).slice(0, PIN_LENGTH);
    setPin(next);
    if (next.length === PIN_LENGTH) void submit(next);
  };

  return (
    <Stack gap={4}>
      <div className="row" style={{ gap: 8 }}>
        <IconButton icon="arrow_left" label="Back to the names" onClick={onBack} />
        <div className="row" style={{ gap: 12 }}>
          <Avatar name={student.name} size={44} />
          <div>
            <p className="eyebrow">Hi, {student.name}</p>
            <h1 className="join__title">Enter your PIN</h1>
          </div>
        </div>
      </div>
      <div className="card card--pad card--raised pin">
        <label className="visually-hidden" htmlFor="pin-input">
          Your {PIN_LENGTH}-digit PIN
        </label>
        <input
          ref={inputRef}
          id="pin-input"
          className="pin__input"
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          value={pin}
          maxLength={PIN_LENGTH}
          onChange={(e) => {
            typedRef.current = true;
            const v = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
            setPin(v);
            setError(null);
            if (v.length === PIN_LENGTH) void submit(v);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'pin-error' : undefined}
        />
        <motion.div className="pin__dots" key={shake} animate={shake ? { x: [0, -10, 10, -6, 6, 0] } : undefined} transition={{ duration: 0.35 }} aria-hidden>
          {Array.from({ length: PIN_LENGTH }, (_, i) => (
            <span key={i} className={i < pin.length ? 'pin__dot is-filled' : 'pin__dot'} />
          ))}
        </motion.div>
        <p id="pin-error" className="pin__error" role="alert">
          {error ?? (busy ? 'Checking your PIN' : ' ')}
        </p>
        <div className="pin__pad" role="group" aria-label="Number pad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" className="pin__key" onClick={() => press(d)} disabled={busy}>
              {d}
            </button>
          ))}
          <span aria-hidden />
          <button type="button" className="pin__key" onClick={() => press('0')} disabled={busy}>
            0
          </button>
          <button type="button" className="pin__key pin__key--icon" onClick={() => setPin((p) => p.slice(0, -1))} disabled={busy || pin.length === 0} aria-label="Delete last digit">
            <Icon name="backspace" size={24} />
          </button>
        </div>
      </div>
      <p className="faint">Your PIN is on the card from your teacher. Keep it private.</p>
    </Stack>
  );
}
